import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../config/redux/reducers";
import { fetchDriverSections, rejectDriver, approveDriver } from "../../redux/onBoardingSlice";
import type { PortalFilterElement } from "../../redux/onBoardingSlice.types";
import type { RejectionPayload } from "../DriverVerificationPanel/types";

export interface UseDriverVerificationDetailsProps {
  open: boolean;
  data: PortalFilterElement;
  onClose: () => void;
  onReject: (payload: RejectionPayload) => void;
  onApprove: () => void;
}

/**
 * Encapsulates all Redux interactions for DriverVerificationDetails.
 *
 * Separating this from rendering means:
 * - The hook is testable with `renderHook` + a mock store (no MUI/Dialog needed).
 * - The component becomes a thin renderer whose markup can be snapshot-tested.
 */
export const useDriverVerificationDetails = ({
  open,
  data,
  onClose,
  onReject,
  onApprove,
}: UseDriverVerificationDetailsProps) => {
  const dispatch = useDispatch();
  const { driverSections, sectionsLoading } = useSelector(
    (state: AppState) => state.onBoarding
  );

  useEffect(() => {
    if (open && data.userId) {
      dispatch(fetchDriverSections({ userId: data.userId }) as any);
    }
  }, [open, data.userId, dispatch]);

  const handleReject = useCallback(
    (payload: RejectionPayload) => {
      dispatch(
        rejectDriver({ userId: data.userId, rejectedFields: payload.rejectedFields }) as any
      ).then((action: any) => {
        if (rejectDriver.fulfilled.match(action)) {
          onReject(payload);
          onClose();
        }
      });
    },
    [dispatch, data.userId, onReject, onClose]
  );

  const handleApprove = useCallback(() => {
    dispatch(approveDriver({ userId: data.userId }) as any).then((action: any) => {
      if (approveDriver.fulfilled.match(action)) {
        onApprove();
        onClose();
      }
    });
  }, [dispatch, data.userId, onApprove, onClose]);

  return { driverSections, sectionsLoading, handleReject, handleApprove };
};
